import { Calendar, ExternalLink, BookOpen, BadgeCheck, MessageCircle } from 'lucide-react'

// ─── Article update date + verification badge ────────────────

export function ContentMeta({ cityName: _cityName, serviceName }: { cityName: string; serviceName: string }) {
  const now = new Date()
  const month = now.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })

  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-slate-400">
      <span className="flex items-center gap-1">
        <Calendar className="h-3 w-3" />
        Mis à jour en {month}
      </span>
      <span className="flex items-center gap-1">
        <BadgeCheck className="h-3 w-3 text-emerald-500" />
        Vérifié par un expert {serviceName.toLowerCase()}
      </span>
      <span className="flex items-center gap-1">
        <BookOpen className="h-3 w-3" />
        Temps de lecture : 6 min
      </span>
    </div>
  )
}

// ─── Official source citations ───────────────────────────────

const SOURCES: Record<string, Array<{ name: string; url: string; desc: string }>> = {
  plomberie: [
    { name: 'ADEME', url: 'https://www.ademe.fr', desc: 'Agence de la transition écologique — aides et subventions' },
    { name: 'Service-Public.fr', url: 'https://www.service-public.fr/particuliers/vosdroits/F31325', desc: 'Réglementation travaux en copropriété' },
    { name: 'Eau de Paris', url: 'https://www.eaudeparis.fr/qualite-de-leau', desc: 'Qualité et dureté de l\'eau par commune' },
  ],
  electricite: [
    { name: 'Consuel', url: 'https://www.consuel.com', desc: 'Attestation de conformité des installations électriques' },
    { name: 'Promotelec', url: 'https://www.promotelec.com', desc: 'Normes NF C 15-100 et sécurité électrique' },
    { name: 'ADEME', url: 'https://www.ademe.fr', desc: 'Aides photovoltaïque et rénovation énergétique' },
  ],
  peinture: [
    { name: 'ANAH', url: 'https://www.anah.gouv.fr', desc: 'Agence nationale de l\'habitat — aides à la rénovation' },
    { name: 'ADEME', url: 'https://www.ademe.fr', desc: 'Labels et certifications peintures écologiques' },
    { name: 'France Rénov\'', url: 'https://france-renov.gouv.fr', desc: 'Guichet unique de la rénovation énergétique' },
  ],
  renovation: [
    { name: 'France Rénov\'', url: 'https://france-renov.gouv.fr', desc: 'MaPrimeRénov\', CEE, éco-PTZ — toutes les aides' },
    { name: 'ANAH', url: 'https://www.anah.gouv.fr', desc: 'Aides pour les propriétaires modestes' },
    { name: 'Qualibat', url: 'https://www.qualibat.com', desc: 'Certification des artisans du bâtiment' },
  ],
  menuiserie: [
    { name: 'UFME', url: 'https://www.ufme.fr', desc: 'Union des fabricants de menuiseries — normes et labels' },
    { name: 'ADEME', url: 'https://www.ademe.fr', desc: 'Aides au remplacement de fenêtres' },
    { name: 'France Rénov\'', url: 'https://france-renov.gouv.fr', desc: 'Simulateur d\'aides à la rénovation' },
  ],
  toiture: [
    { name: 'FFB Couverture', url: 'https://www.ffbatiment.fr', desc: 'Fédération Française du Bâtiment — section couverture' },
    { name: 'ADEME', url: 'https://www.ademe.fr', desc: 'Isolation des combles et toitures' },
    { name: 'Qualibat', url: 'https://www.qualibat.com', desc: 'Certification RGE des couvreurs' },
  ],
  serrurerie: [
    { name: 'CNPP', url: 'https://www.cnpp.com', desc: 'Certification A2P des serrures et cylindres' },
    { name: 'Service-Public.fr', url: 'https://www.service-public.fr', desc: 'Droits et obligations en cas de cambriolage' },
    { name: 'DGCCRF', url: 'https://www.economie.gouv.fr/dgccrf', desc: 'Protection des consommateurs — tarifs d\'urgence' },
  ],
  climatisation: [
    { name: 'ADEME', url: 'https://www.ademe.fr', desc: 'Guide de la climatisation responsable' },
    { name: 'France Rénov\'', url: 'https://france-renov.gouv.fr', desc: 'Aides pour PAC et climatisation réversible' },
    { name: 'Qualipac', url: 'https://www.qualit-enr.org', desc: 'Certification des installateurs de PAC' },
  ],
}

const DEFAULT_SOURCES = [
  { name: 'France Rénov\'', url: 'https://france-renov.gouv.fr', desc: 'Guichet unique de la rénovation énergétique' },
  { name: 'ADEME', url: 'https://www.ademe.fr', desc: 'Agence de la transition écologique' },
  { name: 'Service-Public.fr', url: 'https://www.service-public.fr', desc: 'Informations administratives officielles' },
]

export function SourceCitations({ serviceSlug }: { serviceSlug: string }) {
  const sources = SOURCES[serviceSlug] ?? DEFAULT_SOURCES

  return (
    <div className="mt-6 rounded-xl border border-slate-200 bg-slate-50 p-4">
      <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
        <ExternalLink className="h-3 w-3" />
        Sources et références officielles
      </p>
      <ul className="mt-2.5 space-y-1.5">
        {sources.map((source) => (
          <li key={source.name} className="flex items-start gap-2 text-xs">
            <span className="mt-0.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
            <div>
              <a
                href={source.url}
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold text-primary hover:underline"
              >
                {source.name}
              </a>
              <span className="text-slate-500"> — {source.desc}</span>
            </div>
          </li>
        ))}
      </ul>
    </div>
  )
}

// ─── « Questions des internautes » — realistic user questions ─

const INTERNAUTE_QUESTIONS: Record<string, (city: string, artisan: string) => Array<{ question: string; answer: string; author: string; date: string }>> = {
  plomberie: (city, artisan) => [
    { question: `Mon ${artisan} me facture un déplacement en plus de l'intervention, est-ce normal ?`, answer: `Oui, les frais de déplacement sont légaux et courants à ${city}. Ils varient de 20 à 50 € selon la distance. Vérifiez qu'ils figurent sur le devis. Si le professionnel intervient dans votre commune, certains les incluent dans le tarif horaire.`, author: 'Marie L.', date: 'il y a 2 semaines' },
    { question: `J'ai une fuite sous l'évier, est-ce que ça peut attendre le lendemain ?`, answer: `Si vous pouvez couper l'arrivée d'eau sous l'évier (vanne d'arrêt) et que la fuite s'arrête, ce n'est pas une urgence — vous pouvez attendre un RDV normal. Si la fuite continue malgré la coupure, c'est une urgence car elle peut provenir de la colonne montante ou d'un tuyau encastré.`, author: 'Thomas R.', date: 'il y a 1 mois' },
    { question: `Le devis mentionne "fournitures" sans détail, dois-je m'inquiéter ?`, answer: `Demandez impérativement le détail des fournitures (marque, référence, quantité). Un devis avec juste "fournitures : 350 €" n'est pas assez précis. Un professionnel sérieux détaille chaque pièce. C'est aussi important pour comparer les devis entre eux.`, author: 'Nathalie D.', date: 'il y a 3 semaines' },
  ],
  electricite: (_city, _artisan) => [
    { question: `Mon tableau électrique a 30 ans, est-il dangereux ?`, answer: `Un tableau de 30 ans avec des fusibles à fil est potentiellement dangereux : absence de protection différentielle 30mA, pas de mise à la terre fiable, et risque d'échauffement. Faites faire un diagnostic électrique (150-250 €) pour évaluer l'urgence du remplacement.`, author: 'Philippe M.', date: 'il y a 1 semaine' },
    { question: `Peut-on installer une borne de recharge en appartement ?`, answer: `Oui, c'est le "droit à la prise" (loi Mobilités). Même en copropriété, le syndic ne peut pas refuser sauf motif technique sérieux. L'installation individuelle coûte 1 200-3 000 € et peut être financée à 50 % par le programme ADVENIR.`, author: 'Laurent K.', date: 'il y a 2 semaines' },
    { question: `Combien de prises électriques minimum dans une cuisine ?`, answer: `La norme NF C 15-100 impose au minimum 6 prises dans une cuisine de plus de 4 m², dont 4 au-dessus du plan de travail. En pratique, prévoyez-en 8-10 pour couvrir tous vos appareils (cafetière, grille-pain, robot, chargeurs). C'est lors de la rénovation qu'il faut en prévoir assez.`, author: 'Sophie B.', date: 'il y a 1 mois' },
  ],
  peinture: (_city, _artisan) => [
    { question: `Mon peintre veut appliquer 2 couches mais le mur n'est pas uniforme, c'est normal ?`, answer: `Non, si le mur présente des différences de teinte après 2 couches, c'est souvent un problème de sous-couche insuffisante ou de fond absorbant. Demandez une sous-couche d'accrochage + 2 couches de finition. Les murs neufs ou très absorbants nécessitent parfois 3 couches au total.`, author: 'Isabelle G.', date: 'il y a 3 semaines' },
    { question: `Quelle est la différence entre mat, satiné et brillant ?`, answer: `Le mat cache les défauts mais se salit plus vite — idéal pour plafonds et chambres. Le satiné est le meilleur compromis : lessivable et lumineux — idéal pour séjours et couloirs. Le brillant est très lessivable mais montre tous les défauts — réservé aux boiseries et pièces d'eau.`, author: 'David F.', date: 'il y a 2 semaines' },
    { question: `Les peintures "sans COV" sont-elles vraiment inodores ?`, answer: `Les peintures à moins de 1 g/L de COV sont quasi inodores — vous pouvez dormir dans la pièce le soir même. Les peintures classiques (30-50 g/L) dégagent une odeur forte pendant 2-5 jours. En appartement, les peintures faible COV évitent les conflits avec les voisins.`, author: 'Camille P.', date: 'il y a 1 mois' },
  ],
  renovation: (_city, _artisan) => [
    { question: `Faut-il vider complètement l'appartement pour une rénovation ?`, answer: `Pour une rénovation complète (électricité + plomberie + sols + peinture), oui — impossible de vivre sur le chantier et les poussières sont nocives. Pour un rafraîchissement partiel, le déplacement des meubles pièce par pièce suffit. Prévoyez un garde-meubles (80-200 €/mois) pour une réno complète.`, author: 'Julien M.', date: 'il y a 2 semaines' },
    { question: `Comment savoir si un mur est porteur avant d'ouvrir ?`, answer: `Ne faites JAMAIS d'ouverture sans avis d'un ingénieur structure. Indices d'un mur porteur : épaisseur > 15 cm, mur perpendiculaire aux poutres du plancher, mur en briques pleines ou béton. Les cloisons en plâtre de 7 cm ne sont jamais porteuses. L'étude de structure coûte 500-1 500 € et évite une catastrophe.`, author: 'Anne-Sophie L.', date: 'il y a 1 mois' },
    { question: `Mon artisan demande un acompte de 50 %, c'est excessif ?`, answer: `Un acompte de 30 % à la commande est la norme, 40 % est acceptable pour des matériaux coûteux. Au-delà de 40 %, méfiez-vous. Le paiement doit être échelonné : 30 % à la commande, 40 % à mi-chantier, 30 % à la réception. Ne payez jamais 100 % avant la fin des travaux.`, author: 'Marc V.', date: 'il y a 3 semaines' },
  ],
}

const DEFAULT_QUESTIONS = (_city: string, _artisan: string) => [
  { question: `Comment vérifier que l'artisan est bien assuré ?`, answer: `Demandez une copie de l'attestation d'assurance RC Pro et décennale en cours de validité. Vérifiez que les dates couvrent la période de vos travaux. Vous pouvez aussi vérifier l'existence de l'entreprise sur societe.com avec le numéro SIRET.`, author: 'Pierre H.', date: 'il y a 1 semaine' },
  { question: `Le devis gratuit est-il vraiment sans engagement ?`, answer: `Oui, un devis est une proposition — il ne vous engage à rien tant que vous ne l'avez pas signé. L'artisan ne peut pas facturer le devis sauf s'il vous a prévenu à l'avance (rare et réglementé). Sur AfficheServices, tous les devis sont gratuits et sans engagement.`, author: 'Catherine N.', date: 'il y a 2 semaines' },
  { question: `Peut-on négocier le prix avec un artisan ?`, answer: `La négociation est possible mais délicate. Plutôt que de marchander le prix, demandez si des économies sont possibles : matériaux alternatifs équivalents, regroupement de travaux, période creuse. Comparer 3 devis est le meilleur levier — montrez que vous avez des alternatives.`, author: 'Stéphane R.', date: 'il y a 1 mois' },
]

export function InternaunteQuestions({ serviceSlug, cityName, artisan }: { serviceSlug: string; cityName: string; artisan: string }) {
  const getQuestions = INTERNAUTE_QUESTIONS[serviceSlug] ?? DEFAULT_QUESTIONS
  const questions = getQuestions(cityName, artisan)

  return (
    <div className="mt-8 rounded-2xl border border-border bg-white p-6 shadow-xs">
      <h2 className="flex items-center gap-2 text-lg font-bold text-slate-900">
        <MessageCircle className="h-5 w-5 text-primary" />
        Questions des internautes
      </h2>
      <p className="mt-1 text-xs text-slate-500">Questions fréquemment posées par nos utilisateurs</p>
      <div className="mt-4 space-y-4">
        {questions.map((q, i) => (
          <div key={i} className="rounded-xl border border-slate-100 bg-slate-50 p-4">
            <div className="flex items-start gap-3">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-bold text-primary">
                {q.author.split(' ')[0][0]}{q.author.split(' ')[1]?.[0] ?? ''}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold text-slate-700">{q.author}</span>
                  <span className="text-[10px] text-slate-400">· {q.date}</span>
                </div>
                <p className="mt-1 text-sm font-medium text-slate-900">{q.question}</p>
              </div>
            </div>
            <div className="mt-3 ml-11 rounded-lg bg-white p-3 text-sm leading-relaxed text-slate-600">
              <div className="mb-1 flex items-center gap-1.5">
                <BadgeCheck className="h-3.5 w-3.5 text-primary" />
                <span className="text-[10px] font-semibold text-primary">Réponse vérifiée AfficheServices</span>
              </div>
              {q.answer}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
